/**
 * W1-11b (app-shell-ui): the project menu at the left of the top bar (DESIGN.md §3.1, §4.4): the chart's title and
 * difficulty ("Kyou no Hi  IN Lv.15") open one menu with the project entries other modules place in 'app.project'
 * (Song info, Assets, Version history, Project settings, Import into this project, Export) and the shell's Close,
 * followed by the Edit, View and Help menus as submenus (one row of chrome, no menu bar). The View menu's Layout
 * entry names the layout in effect, and says so when Laptop was applied automatically for the screen size.
 */

import type { ChartMeta } from '../../core/model/types';
import type { LayoutPreset } from '../../state/types';
import { Icon } from '../../ui/Icon';
import { Menu } from '../../ui/Menu';
import type { MenuItem } from '../../ui/types';
import type { AppServices, MenuId } from '../registry/types';
import { menuItemsFor } from './ContextMenuHost';
import { effectiveLayout, LAYOUT_PRESETS } from './LayoutPresets';
import { useDocSelect, useLayoutViewport, useSettingsValue, useStore, useText, type ShellText } from './ShellContext';
import { m } from './shell.messages';
import s from './Shell.module.css';

const SUBMENUS: ReadonlyArray<readonly [MenuId, typeof m.menuEdit]> = [
  ['app.edit', m.menuEdit],
  ['app.view', m.menuView],
  ['app.help', m.menuHelp],
];

const selectMeta = (doc: { readonly meta: ChartMeta }): ChartMeta => doc.meta;

/** The layout preset in effect and whether it was applied automatically (Laptop on a small screen). */
export interface LayoutInEffect {
  readonly preset: LayoutPreset;
  readonly auto: boolean;
}

/** "Layout: Compose", or the explanation that the Laptop layout was applied automatically. */
export function layoutMenuLabel(layout: LayoutInEffect, text: ShellText): string {
  return text.t(m.labelValue, {
    label: text.t(m.cmdLayout),
    value: layout.auto ? text.t(m.layoutAuto) : text.t(LAYOUT_PRESETS[layout.preset].title),
  });
}

/**
 * The project menu's items: 'app.project' entries, then the Edit / View / Help submenus (when not empty); with
 * `layout`, the View menu's Layout entry names the layout in effect.
 */
export function projectMenuItems(services: AppServices, text: ShellText, layout?: LayoutInEffect): MenuItem[] {
  const items: MenuItem[] = menuItemsFor(services.registry, 'app.project');
  const subs: MenuItem[] = [];
  for (const [id, title] of SUBMENUS) {
    const sub = menuItemsFor(services.registry, id).map((item): MenuItem =>
      layout !== undefined && item.kind === 'command' && item.command === 'view.layout'
        ? { ...item, label: layoutMenuLabel(layout, text) }
        : item,
    );
    if (sub.length > 0) subs.push({ kind: 'submenu', label: text.t(title), items: sub });
  }
  if (items.length > 0 && subs.length > 0) items.push({ kind: 'separator' });
  return [...items, ...subs];
}

export function ProjectMenu(props: { readonly services: AppServices }) {
  const { services } = props;
  const text = useText(services);
  const store = useStore(services);
  const meta = useDocSelect<ChartMeta | null>(store, ['meta'], selectMeta, null);
  const name = meta === null || meta.name.trim() === '' ? text.t(m.untitled) : meta.name;
  const level = meta?.level.trim() ?? '';
  const viewport = useLayoutViewport(services);
  const chosen = useSettingsValue(services).editing.layout;
  const preset = effectiveLayout(chosen, viewport);
  return (
    <Menu
      label={text.t(m.projectMenu)}
      trigger={
        <button
          type="button"
          className={s.projectButton}
          aria-label={text.t(m.projectMenuLabel, { name, level: level === '' ? 'none' : level })}
        >
          <Icon name="Menu" size={16} />
          <span className={s.projectName}>{name}</span>
          {level !== '' && <span className={s.projectLevel}>{level}</span>}
          <Icon name="ChevronDown" size={12} />
        </button>
      }
      items={projectMenuItems(services, text, { preset, auto: preset !== chosen })}
    />
  );
}
